import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { useParams, useNavigate } from "react-router-dom";
import { useJobContext } from "../Context/JobContext"; // Import context

const AddNewJob = () => {
  const { id } = useParams(); // Ambil ID dari URL
  const { selectedJob, editJob } = useJobContext(); // Menggunakan context untuk mendapatkan data pekerjaan
  const [formData, setFormData] = useState({
    title: "",
    job_description: "",
    job_qualification: "",
    job_type: "",
    job_tenure: "",
    job_status: 1,
    company_name: "",
    company_image_url: "",
    company_city: "",
    salary_min: "",
    salary_max: "",
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Jika ada id, lakukan edit (mode edit)
  useEffect(() => {
    if (id) {
      editJob(id); // Ambil data job yang sesuai dengan id
    }
  }, [id, editJob]);

  // Set form data jika selectedJob sudah ada
  useEffect(() => {
    if (selectedJob) {
      setFormData({
        title: selectedJob.title,
        job_description: selectedJob.job_description,
        job_qualification: selectedJob.job_qualification,
        job_type: selectedJob.job_type,
        job_tenure: selectedJob.job_tenure,
        job_status: selectedJob.job_status,
        company_name: selectedJob.company_name,
        company_image_url: selectedJob.company_image_url,
        company_city: selectedJob.company_city,
        salary_min: selectedJob.salary_min,
        salary_max: selectedJob.salary_max,
      });
    }
  }, [selectedJob]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const token = Cookies.get("token"); // Ambil token dari cookies
      if (!token) {
        alert("Authorization required");
        return;
      }

      // Check if in edit mode (i.e., `id` exists)
      if (id) {
        // PUT request to the API to update the job data
        fetch(`https://final-project-api-alpha.vercel.app/api/jobs/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        })
          .then((response) => response.json())
          .then((data) => {
            alert("Job vacancy data has been successfully edited");
            setFormData({
              title: "",
              job_description: "",
              job_qualification: "",
              job_type: "",
              job_tenure: "",
              job_status: 1,
              company_name: "",
              company_image_url: "",
              company_city: "",
              salary_min: "",
              salary_max: "",
            });
            navigate("/dashboard/list-job-vacancy");
            window.location.reload();
          })
          .catch((error) => {
            console.error("Error updating job:", error);
            alert("Failed to update job vacancy. Please try again.");
          });
      } else {
        // POST request to the API to add a new job
        fetch("https://final-project-api-alpha.vercel.app/api/jobs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        })
          .then((response) => response.json())
          .then((data) => {
            alert("Job Vacancies have been successfully added!");
            setFormData({
              title: "",
              job_description: "",
              job_qualification: "",
              job_type: "",
              job_tenure: "",
              job_status: 1,
              company_name: "",
              company_image_url: "",
              company_city: "",
              salary_min: "",
              salary_max: "",
            });
            navigate("/dashboard/list-job-vacancy");
          })
          .catch((error) => {
            console.error("Error adding job:", error);
            alert("Failed to add job vacancy. Please try again.");
          });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Job title is required";
    if (!formData.job_description) newErrors.job_description = "Job description is required";
    if (!formData.job_qualification) newErrors.job_qualification = "Job qualifications are required";
    if (!formData.job_type) newErrors.job_type = "Job type is required";
    if (!formData.job_tenure) newErrors.job_tenure = "Job tenure is required";
    if (!formData.company_name) newErrors.company_name = "Company name is required";
    if (!formData.company_image_url) newErrors.company_image_url = "Company logo URL is required";
    if (!formData.company_city) newErrors.company_city = "Company city is required";
    if (!formData.salary_min || isNaN(formData.salary_min)) newErrors.salary_min = "Valid minimum salary is required";
    if (!formData.salary_max || isNaN(formData.salary_max)) newErrors.salary_max = "Valid maximum salary is required";
    if (Number(formData.salary_min) > Number(formData.salary_max)) newErrors.salary_range = "Salary min should be less than salary max";
    if (formData.salary_min === formData.salary_max) newErrors.salary_range = "Salary min and max should not be the same";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <main className="flex h-screen">
      <section className="flex-1 p-6 ml-[300px] max-md:ml-[250px] max-sm:ml-[50px] h-full overflow-auto max-sm:mt-16">
        <h1 className="text-2xl font-bold text-indigo-700">
          {id ? "Edit Job Vacancy" : "Add New Job"}
        </h1>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Job Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
          </div>

          {/* Job Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Job Description</label>
            <textarea
              name="job_description"
              value={formData.job_description}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows="4"
            ></textarea>
            {errors.job_description && <p className="text-red-500 text-xs">{errors.job_description}</p>}
          </div>

          {/* Job Qualification */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Job Qualification</label>
            <textarea
              name="job_qualification"
              value={formData.job_qualification}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows="3"
            ></textarea>
            {errors.job_qualification && <p className="text-red-500 text-xs">{errors.job_qualification}</p>}
          </div>

          {/* Job Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Job Type</label>
            <select
              name="job_type"
              value={formData.job_type}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">Select Job Type</option>
              <option value="Remote">Remote</option>
              <option value="On-site">On-site</option>
              <option value="Hybrid">Hybrid</option>
            </select>
            {errors.job_type && <p className="text-red-500 text-xs">{errors.job_type}</p>}
          </div>

                    {/* Job Tenure */}
                    <div>
            <label className="block text-sm font-medium text-gray-700">Job Tenure</label>
            <select
              name="job_tenure"
              value={formData.job_tenure}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">Select Tenure</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
            </select>
            {errors.job_tenure && <p className="text-red-500 text-xs">{errors.job_tenure}</p>}
          </div>

          {/* Job Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Job Status</label>
            <select
              name="job_status"
              value={formData.job_status}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
            {errors.job_status && <p className="text-red-500 text-xs">{errors.job_status}</p>}
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Company Name</label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.company_name && <p className="text-red-500 text-xs">{errors.company_name}</p>}
          </div>

          {/* Company Logo URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Company Logo URL</label>
            <input
              type="url"
              name="company_image_url"
              value={formData.company_image_url}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.company_image_url && <p className="text-red-500 text-xs">{errors.company_image_url}</p>}
          </div>

          {/* Company City */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Company City</label>
            <input
              type="text"
              name="company_city"
              value={formData.company_city}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.company_city && <p className="text-red-500 text-xs">{errors.company_city}</p>}
          </div>

          {/* Salary Min */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Salary Min</label>
            <input
              type="number"
              name="salary_min"
              value={formData.salary_min}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.salary_min && <p className="text-red-500 text-xs">{errors.salary_min}</p>}
          </div>

          {/* Salary Max */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Salary Max</label>
            <input
              type="number"
              name="salary_max"
              value={formData.salary_max}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.salary_max && <p className="text-red-500 text-xs">{errors.salary_max}</p>}
          </div>

          <div className="mt-6">
            <button
              type="submit"
              className="w-full p-3 bg-blue-600 text-white rounded-md"
            >
              {id ? "Save Changes" : "Submit"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default AddNewJob;
